import { db } from '../firebase';
import { groqChatCompletion } from './groqClient';
import {
  collection, addDoc, getDocs, doc, getDoc, query, where,
  updateDoc, setDoc, serverTimestamp, arrayUnion, arrayRemove, deleteField
} from 'firebase/firestore';

// The flags feature shipped with status values 'approved'/'rejected' long
// before the clearer 'valid'/'invalid' naming below existed — some of the
// 50+ disputes already resolved since May are stamped with the old values.
// Every place that reads a resolved status must recognize both, or those
// pre-existing records simply vanish (they match neither 'pending' nor
// 'valid'/'invalid').
function isValidStatus(status) {
  return status === 'valid' || status === 'approved';
}
function isInvalidStatus(status) {
  return status === 'invalid' || status === 'rejected';
}

// ─── Allowlist ────────────────────────────────────────────────────────────────

let _allowlistCache = null;

export async function loadAllowlist() {
  if (_allowlistCache) return _allowlistCache;
  try {
    const snap = await getDoc(doc(db, 'validWords', 'allowlist'));
    _allowlistCache = snap.exists() ? snap.data() : {};
    return _allowlistCache;
  } catch {
    return {};
  }
}

export function clearAllowlistCache() {
  _allowlistCache = null;
}

/** Returns confirmed valid words for a given letter + category */
export function getAllowlistExamples(allowlist, letter, category) {
  return allowlist?.[letter.toUpperCase()]?.[category] || [];
}

// ─── Flag submission ──────────────────────────────────────────────────────────

/**
 * Writes a single disputed answer to the flags collection.
 * @param {{ answer, category, letter, botKey }} flagData
 */
export async function submitFlag({ answer, category, letter, botKey }) {
  const userId = localStorage.getItem('currentUserId') || 'anonymous';
  await addDoc(collection(db, 'flags'), {
    answer: answer.trim(),
    category,
    letter: letter.toUpperCase(),
    botKey: botKey || 'unknown',
    userId,
    timestamp: serverTimestamp(),
    status: 'pending',
  });
}

// ─── Batch re-review (one call per round) ────────────────────────────────────

/**
 * Re-reviews all flagged answers for a round in a single Groq call.
 * Only called once per round, regardless of how many answers were flagged.
 *
 * @param {string} letter
 * @param {Array<{ category, answer }>} flaggedItems
 * @param {Object} allowlist
 * @returns {Promise<Object>} — { [category]: number } updated scores (only flagged categories)
 */
export async function batchReReview(letter, flaggedItems, allowlist) {
  if (!flaggedItems.length) return {};

  // Build known-valid examples for flagged categories
  const knownLines = flaggedItems
    .map(({ category }) => {
      const words = getAllowlistExamples(allowlist, letter, category);
      return words.length
        ? `  ${category} (confirmed valid examples): ${words.join(', ')}`
        : null;
    })
    .filter(Boolean)
    .join('\n');

  const disputedList = flaggedItems
    .map(({ category, answer }) => `  - ${category}: "${answer}"`)
    .join('\n');

  const exampleJson = Object.fromEntries(
    flaggedItems.map(({ category }) => [category, { valid: true, score: 8 }])
  );

  const prompt = `You are re-reviewing disputed answers for a word game. Letter: "${letter}".

IMPORTANT — Cultural awareness:
This game is played globally. Nigerian names (Yoruba, Igbo, Hausa: Olamide, Ngozi, Emeka), African places, Arabic, Indian, Asian and Latin American answers are ALL valid. Give the benefit of the doubt on culturally specific answers.
${knownLines ? `\nAdmin-confirmed valid words:\n${knownLines}` : ''}

The player disputed these answers (all scored 0):
${disputedList}

For each, decide if it is actually a valid example of the category. Score 0 if truly invalid, or 5-15 if valid (higher = more rare/impressive).

Return ONLY valid JSON in this exact shape:
${JSON.stringify(exampleJson, null, 2)}`;

  try {
    const raw = await groqChatCompletion({
      messages: [{ role: 'user', content: prompt }],
      model: 'qwen/qwen3.8-27b',
      reasoning_effort: 'none',
      temperature: 0.1,
      max_tokens: 150,
      response_format: { type: 'json_object' },
    });

    const parsed = JSON.parse(raw);

    // Extract updated scores for flagged categories only
    const updatedScores = {};
    for (const { category } of flaggedItems) {
      const entry = parsed[category];
      if (entry && entry.valid) {
        updatedScores[category] = Math.max(1, Math.min(15, parseInt(entry.score) || 7));
      } else {
        updatedScores[category] = 0; // still invalid — keep at 0
      }
    }
    return updatedScores;
  } catch (err) {
    console.error('[FlagService] Re-review failed:', err);
    return {};
  }
}

// ─── Admin: load pending flags ────────────────────────────────────────────────

export async function loadPendingFlags() {
  try {
    // Fetch all flags without a composite index, filter + sort in JS
    const snap = await getDocs(collection(db, 'flags'));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((f) => f.status === 'pending')
      .sort((a, b) => (b.timestamp?.seconds ?? 0) - (a.timestamp?.seconds ?? 0));
  } catch (err) {
    console.error('[FlagService] loadPendingFlags failed:', err);
    return [];
  }
}

// ─── Admin: load resolved flags (history) ─────────────────────────────────────

export async function loadResolvedFlags() {
  // Filtered server-side (a single 'in' clause needs no composite index)
  // instead of fetching the whole collection and filtering in the browser —
  // resolved flags only ever accumulate, so that read cost and latency
  // would keep growing indefinitely.
  const snap = await getDocs(
    query(collection(db, 'flags'), where('status', 'in', ['valid', 'invalid', 'approved', 'rejected']))
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .map((f) => ({ ...f, status: isValidStatus(f.status) ? 'valid' : 'invalid' }))
    .sort((a, b) => (b.resolvedAt?.seconds ?? b.timestamp?.seconds ?? 0) - (a.resolvedAt?.seconds ?? a.timestamp?.seconds ?? 0));
}

// ─── Admin: mark an answer as actually valid → add to allowlist ───────────────
//
// Named for what the decision MEANS (the disputed answer is a real, valid
// example of the category), not the old generic "approve" — a reviewer
// reading "Approve" with no other context could easily read it as "approve
// that this was correctly marked wrong" instead of the opposite.

export async function markAnswerValid(flagId, { answer, letter, category }) {
  await updateDoc(doc(db, 'flags', flagId), { status: 'valid', resolvedAt: serverTimestamp() });

  // Add to validWords/allowlist — use setDoc with merge so it works whether doc exists or not
  const allowlistRef = doc(db, 'validWords', 'allowlist');
  await setDoc(
    allowlistRef,
    { [letter.toUpperCase()]: { [category]: arrayUnion(answer) } },
    { merge: true }
  );

  clearAllowlistCache();
}

// ─── Admin: mark an answer as correctly invalid ───────────────────────────────

export async function markAnswerInvalid(flagId) {
  await updateDoc(doc(db, 'flags', flagId), { status: 'invalid', resolvedAt: serverTimestamp() });
}

// ─── Admin: undo a past decision ───────────────────────────────────────────────
//
// Puts the flag back in the pending queue. If it had been marked valid (and
// therefore added to the allowlist), also removes it from the allowlist —
// otherwise "undo" would silently leave the word permanently auto-approved
// for future AI reviews even though the admin reversed their decision.

export async function undoFlagResolution(flagId, { answer, letter, category, previousStatus }) {
  // Remove from the allowlist FIRST, and let a real failure (permissions,
  // network) propagate instead of being swallowed — the flag's own status
  // only flips to 'pending' once that removal has actually succeeded.
  // Doing it in the other order (as before) meant a failed removal still
  // reported as a successful undo, while the word stayed permanently valid
  // for every future AI review despite the UI showing it as reversed.
  if (isValidStatus(previousStatus)) {
    const allowlistRef = doc(db, 'validWords', 'allowlist');
    const snap = await getDoc(allowlistRef);
    if (snap.exists()) {
      await updateDoc(allowlistRef, {
        [`${letter.toUpperCase()}.${category}`]: arrayRemove(answer),
      });
    }
    clearAllowlistCache();
  }

  await updateDoc(doc(db, 'flags', flagId), { status: 'pending', resolvedAt: deleteField() });
}
